import CheckmarkIcon from '@assets/icons/checkmark-thin.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/checkbox';
import { createStyles } from '@/contexts/theme-context';
import { formatDateWithYear } from '@/utils/date';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { List } from '@/components/list';
import { ListItemSeparator } from '@/components/list-item';
import { ModalContainer } from '@/components/modal-container';
import { MultiStateCheckbox } from '@/components/multi-state-checkbox';
import { StackScreenProps } from '@react-navigation/stack';
import {
  TAG_PRINT_BATCH_DETAIL_QUERY,
  TAG_PRINT_BATCH_LIST_QUERY,
} from '@/components/tag-print-batch-queries';
import { TAG_PRINT_BATCH_ROUTES } from '@/navigators/routes';
import { TagPrintBatchStackParams } from '@/navigators/types';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useToasts } from '@/contexts/toast-context';
import { View } from 'react-native';

export const ASSIGN_PRINT_TAG_BATCH_PLANOGRAMS_MUTATION = gql`
  mutation AssignPrintTagBatch_Planograms(
    $planogramUUIDs: [uuid!]!
    $printTagBatchUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    assignPrintTagBatchPlanograms(
      planogram_ids: $planogramUUIDs
      print_tag_batch_id: $printTagBatchUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      print_tag_batch_id
    }
  }
`;

export type Props = StackScreenProps<
  TagPrintBatchStackParams,
  TAG_PRINT_BATCH_ROUTES.ImportTemplate
>;

export const ImportTagPrintTemplateScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { planograms, printTagBatchUUID } = route.params;

  const [selectedPlanograms, setSelectedPlanograms] = useState<Set<string>>(
    new Set(),
  );

  const [styles, theme] = useStyles();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { showErrorToast, showToast } = useToasts();

  const onPlanogramCheckboxSelect = (uuid: string) => {
    setSelectedPlanograms((prevState) => {
      const newSet = new Set(prevState);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  const onSelectAllPlanograms = () => {
    setSelectedPlanograms(
      selectedPlanograms.size === planograms.length
        ? new Set()
        : new Set(planograms.map(({ value }) => value as string)),
    );
  };

  const partialPlanogramsSelected = Boolean(
    selectedPlanograms.size > 0 && selectedPlanograms.size < planograms.length,
  );

  const selectedPlanogramsTagsCount = useMemo(
    () =>
      planograms.reduce((acc, planogram) => {
        if (selectedPlanograms.has(planogram.value as string)) {
          acc +=
            planogram.metadata?.planogram_variant_packs_aggregate.aggregate?.sum
              ?.quantity ?? 0;
          return acc;
        }
        return acc;
      }, 0),
    [planograms, selectedPlanograms],
  );

  const [assignPrintTagBatchPlanograms, { loading }] = useMutation(
    ASSIGN_PRINT_TAG_BATCH_PLANOGRAMS_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        getOperationName(TAG_PRINT_BATCH_DETAIL_QUERY) as string,
        getOperationName(TAG_PRINT_BATCH_LIST_QUERY) as string,
      ],
    },
  );

  const onImportSelectedPlanograms = async () => {
    try {
      await assignPrintTagBatchPlanograms({
        variables: {
          planogramUUIDs: [...selectedPlanograms],
          printTagBatchUUID,
          restaurantUUID,
          supplierUUID,
        },
      });
      showToast({
        content:
          selectedPlanogramsTagsCount === 1
            ? i18n.t('toast.tag_imported.success')
            : i18n.t('toast.tags_imported.success', {
                count: selectedPlanogramsTagsCount,
              }),
        type: Type.POSITIVE,
      });
      navigation.goBack();
    } catch {
      showErrorToast();
    }
  };

  return (
    <ModalContainer
      navigationHeaderProps={{
        onBackPress: navigation.goBack,
      }}
    >
      <View style={styles.content}>
        <View style={styles.wideRow}>
          <Typography variant="heading-lg">
            {i18n.t('import_template')}
          </Typography>
        </View>
        <View style={[styles.row, styles.wideRow]}>
          <Typography variant="paragraph-base">
            {i18n.t('select_all')}
          </Typography>
          <MultiStateCheckbox
            isPartiallySelected={partialPlanogramsSelected}
            isSelected={
              partialPlanogramsSelected ||
              selectedPlanograms.size === planograms.length
            }
            onSelect={onSelectAllPlanograms}
            trackingData={{
              name: 'select_all_planograms.checkbox',
            }}
          />
        </View>
        <List
          data={planograms}
          ItemSeparatorComponent={() => (
            <ListItemSeparator style={styles.planogramsListItem} />
          )}
          listItemStyle={styles.wideRow}
          onSelect={({ value }) => {
            onPlanogramCheckboxSelect(value as string);
          }}
          renderMainContent={({ metadata, title }) => {
            const tagCount =
              metadata?.planogram_variant_packs_aggregate?.aggregate?.sum
                ?.quantity ?? 0;
            return (
              <View collapsable={false}>
                <Typography variant="paragraph-base-600">{title}</Typography>
                <Typography variant="paragraph-md">
                  {`${i18n.t('created_date', {
                    date: formatDateWithYear(metadata?.created_at),
                  })} • ${
                    tagCount === 1
                      ? i18n.t('tag_count.singular')
                      : i18n.t('tag_count.plural', {
                          count: tagCount,
                        })
                  }`}
                </Typography>
              </View>
            );
          }}
          renderRightComponent={({ value }) => (
            <Checkbox
              checkProps={{
                stroke: theme.colors.accentText,
              }}
              getCheckboxStyle={(selected) => {
                return [
                  styles.checkboxCheck,
                  selected ? styles.checkboxCheckSelected : null,
                ];
              }}
              icon={
                <CheckmarkIcon
                  height={18}
                  stroke={theme.colors.white}
                  width={18}
                />
              }
              isSelected={selectedPlanograms.has(value as string)}
              onSelect={() => onPlanogramCheckboxSelect(value as string)}
              style={styles.checkbox}
              trackingData={{
                name: 'select_planogram.checkbox',
                properties: {
                  planogramUUID: value,
                },
              }}
            />
          )}
        />

        {selectedPlanograms.size > 0 ? (
          <View style={styles.footer}>
            <Button
              loading={loading}
              onPress={onImportSelectedPlanograms}
              trackingData={{
                name: 'import_selected_planograms.button',
                properties: {
                  printTagBatchUUID,
                },
              }}
            >
              {selectedPlanogramsTagsCount === 1
                ? i18n.t('import_tags_singular')
                : i18n.t('import_tags_plural', {
                    count: selectedPlanogramsTagsCount,
                  })}
            </Button>
          </View>
        ) : null}
      </View>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  checkbox: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  checkboxCheck: {
    backgroundColor: theme.colors.white,
    borderWidth: 1,
    height: 24,
    width: 24,
  },
  checkboxCheckSelected: {
    backgroundColor: theme.colors.accent,
    borderColor: theme.colors.accent,
  },
  content: {
    flex: 1,
  },
  footer: {
    backgroundColor: theme.colors.white,
    paddingBottom: 32,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  planogramsListItem: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: 16,
  },
  row: {
    alignItems: 'center',
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 16,
  },
  wideRow: {
    paddingHorizontal: 16,
  },
}));
